import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useMobile } from 'common-nextjs';
import { KnownCookie } from 'common-types';
import React, { ReactNode, useContext } from 'react';
import { useCookie } from 'react-use';
import FeatheredOverflowContainer from '~/components/FeatheredOverflowContainer';
import ItemThumbnailGridWithAdsLessColumns from '~/components/ItemGrids/ItemThumbnailGridWithAdsLessColumns';
import WebPageItemsSchema from '~/components/ItemGrids/WebPageItemsSchema';
import SeoPagination from '~/components/Pagination/SEOPagination';
import FilteringList from '~/components/Search/FilteringList/FilteringList';
import FilteringListContainer from '~/components/Search/FilteringList/FilteringListContainer';
import FloatingFilterButton from '~/components/Search/FilteringList/FloatingFilterButton';
import FilterKeywordSearch from '~/components/Search/FilteringList/Sections/FilterKeywordSearch';
import PinnedKeyDetails from '~/components/Search/PinnedKeyDetails';
import QueryGrabberButton from '~/components/Search/QueryGrabber/QueryGrabberButton';
import ResultCount from '~/components/Search/ResultCount';
import SavedSearchCta from '~/components/Search/SavedSearchCta';
import ScrollToTopFab from '~/components/Search/ScrollToTopFAB';
import TopDetailSelection from '~/components/Search/TopDetailSelection';
import { BrowseItemsContext } from '~/contexts/BrowseItemsContext';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import useHasFeature from '~/hooks/useHasFeature';
import useRouteQuery from '~/hooks/useRouteQuery';

interface Props {
  headerType?: 'h1' | 'h2' | 'h3';
}

const ResultsGridBlock: React.FC<Props> = ({ headerType }) => {
  const mobile = useMobile();
  const { items, paging } = useContext(BrowseItemsContext) || {};
  const { lander, notFoundMetadata } = useFacetFiltering();
  const { query } = useRouteQuery<any>();
  const [fastShipping] = useCookie(
    'fast_shipping_filter_enabled' as KnownCookie,
  );
  const hasQueryGrabber = useHasFeature('query_grabber');

  if (!items || !paging) {
    return null;
  }

  const hasItems = paging.total_count > 0 || items.length > 0;

  let showAdditionalCopy = false;
  if (fastShipping === '1' && paging.page >= paging.total_pages) {
    showAdditionalCopy = true;
  }

  let itemGrid: ReactNode;
  let itemSection: ReactNode;
  let topSection: React.ReactNode;

  if (hasItems) {
    itemGrid = (
      <>
        {notFoundMetadata && (
          <div className="mt-8">
            <div className="mb-2 text-xl font-semibold">
              No exact matches found
            </div>

            <div>
              Save this search to receive alerts when the next one is listed, or
              check back later.
            </div>

            <hr className="my-6" />

            <div className="mb-6 flex-wrap items-center gap-4 md:flex">
              <span className="mr-6 font-semibold">
                More results for {notFoundMetadata.title}
              </span>

              <a
                href={notFoundMetadata.url}
                className="text-blue-500 underline"
              >
                See More <ArrowRightIcon className="inline-block h-4 w-4" />
              </a>
            </div>
          </div>
        )}

        <ItemThumbnailGridWithAdsLessColumns
          adSlots={[
            'sidelineswap_incontent_1',
            'sidelineswap_incontent_2',
            'sidelineswap_incontent_3',
          ]}
          items={items}
        />
      </>
    );
  } else {
    itemGrid = (
      <div className="text-grey-800 my-12 w-full text-center text-2xl font-semibold">
        No Results
      </div>
    );
  }

  if (mobile) {
    itemSection = <>{itemGrid}</>;
  } else {
    itemSection = (
      <div className="flex space-x-4">
        <FilteringListContainer>
          <FilteringList showKeywordSearch showDeliveryFilter showFindADeal />
        </FilteringListContainer>

        <div className="flex-1">{itemGrid}</div>
      </div>
    );
  }

  if (mobile) {
    topSection = (
      <div>
        <FeatheredOverflowContainer className="flex gap-2">
          <FloatingFilterButton
            showDeliveryFilter
            showFindADeal
            showKeywordSearch
          />

          <div className="h-10 border-r" />

          <PinnedKeyDetails />
        </FeatheredOverflowContainer>

        <div className="my-4 flex items-center">
          <ResultCount paging={paging} />
          <div className="flex-1" />
          {!query.stid && <SavedSearchCta />}
        </div>
      </div>
    );
  } else {
    topSection = (
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilteringListContainer className="mr-2">
          <FilterKeywordSearch />
        </FilteringListContainer>

        <PinnedKeyDetails />

        <div className="flex-1" />

        {!query.stid && <SavedSearchCta />}

        {!query.stid && <ResultCount paging={paging} />}
        {hasQueryGrabber && <QueryGrabberButton />}
      </div>
    );
  }

  return (
    <>
      <WebPageItemsSchema items={items} lander={lander} />
      {!query.stid && <TopDetailSelection headerType={headerType} />}

      <div className="container">
        {topSection}
        {itemSection}

        {showAdditionalCopy && (
          <div className="mb-8 mt-6 text-center text-xl">
            To see more results, remove the <b>Delivery</b> filter.
          </div>
        )}

        <SeoPagination paging={paging} />

        <ScrollToTopFab />
      </div>
    </>
  );
};

export default ResultsGridBlock;
